import { Box, Heading, HStack, Text, type TextProps } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { type ReactNode } from "react";
import { sectionIndex, sections, type SectionId } from "../data/sections";
import { MotionFade } from "./MotionFade";

const MotionBox = motion(Box);

export const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export function Accent(props: TextProps) {
  return (
    <Text
      as="em"
      fontFamily="accent"
      fontStyle="italic"
      fontWeight="400"
      fontSize="1.08em"
      letterSpacing="-0.01em"
      color="accent.300"
      {...props}
    />
  );
}

export function Eyebrow({ index, label }: { index: string; label: string }) {
  return (
    <HStack
      spacing={3}
      fontFamily="mono"
      fontSize="xs"
      letterSpacing="0.16em"
      textTransform="uppercase"
    >
      <Text as="span" color="accent.300">
        {index}
      </Text>
      <Box w="32px" h="1px" bg="accent.400" opacity={0.55} />
      <Text as="span" color="graphite.300">
        {label}
      </Text>
    </HStack>
  );
}

type SectionHeadingProps = {
  section: SectionId;
  title: ReactNode;
  lead?: string;
};

export function SectionHeading({ section, title, lead }: SectionHeadingProps) {
  const label = sections.find((s) => s.id === section)?.label ?? "";

  return (
    <Box mb={{ base: 12, md: 16 }}>
      <MotionFade mb={5}>
        <Eyebrow index={sectionIndex(section)} label={label} />
      </MotionFade>

      {/* The mask is the element observed; the heading inside starts clipped out of view. */}
      <MotionBox
        overflow="hidden"
        pb="0.14em"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-72px" }}
      >
        <MotionBox
          variants={{
            hidden: { y: "108%" },
            show: { y: 0, transition: { duration: 0.8, ease: EASE_OUT } },
          }}
        >
          <Heading
            as="h2"
            fontSize={{ base: "2.1rem", sm: "2.6rem", md: "3.25rem", lg: "3.75rem" }}
            lineHeight="1.04"
            letterSpacing="-0.035em"
            color="white"
            maxW="18ch"
          >
            {title}
          </Heading>
        </MotionBox>
      </MotionBox>

      {lead ? (
        <MotionFade delay={0.12}>
          <Text mt={5} fontSize={{ base: "md", md: "lg" }} lineHeight="tall" color="graphite.400" maxW="2xl">
            {lead}
          </Text>
        </MotionFade>
      ) : null}
    </Box>
  );
}
